import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { Table, TableBody, TableHead, Toolbar, Tooltip } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { ReactComponent as AddIcon } from 'assets/icons/add-red.svg'
import { useHistory } from 'react-router-dom'
import queryString from 'query-string'
import { ContainedButton } from 'components/Shared/ContainedButton'
import { Paginator, RowsNumberSelector } from '../pagination'
import { deepFind } from 'components/Shared/Table/TableBodyRow/utils'
import { backgroundColor } from 'styles/variables'
import { useErrorHandling } from 'context/errorHandling'
import { useTableTabStyles } from './styles'
import {
  getColumnsVisibilityFromLocalStorage,
  getFiltersFromLocalStorage,
  getPageFromLocalStorage,
  getRowsPerPageFromLocalStorage,
  getSortFromLocalStorage,
  mergeColumnsVisibilityWithColumns,
  saveColumnsVisibilityInLocalStorage,
  saveFiltersInLocalStorage,
  savePageInLocalStorage,
  saveRowsPerPageInLocalStorage,
  saveSortInLocalStorage
} from '../preferences'
import TableBodyRow from '../TableBodyRow'
import TableHeadRow from '../TableHeadRow'
import TableFiltersRow from '../TableFiltersRow'
import { TableContainerWrapper } from 'components/Shared/Table/TableContainerWrapper'
import { tableAndTableTabCommonPropTypes, tableTabPropTypes } from '../types'
import { RowMenuOptionsProvider } from './rowMenuOptions'

const TableTab = ({
  columns,
  tabKey,
  tabId,
  pageResponse = null,
  highlightedIds = [],
  onPageResponseChange,
  onHighlightedIdsChange,
  title,
  addButton,
  fetchData,
  onRowClick,
  renderToolbarLeftPanel = () => null,
  renderBottomExtraRow = () => null,
  sort: sortConfig = {
    defaultSortParameters: [],
    columnsGroupedBy: []
  },
  backgroundRefresh = {
    shouldHighlightRow: () => false
  },
  hiddenFilters = [],
  selection,
  menu,
  tableRef
}) => {
  const { t } = useTranslation()
  const { handleError } = useErrorHandling()

  const history = useHistory()
  const classes = useTableTabStyles()

  const [page, setPage] = useState(getPageFromLocalStorage(tabKey))
  const [rowsPerPage, setRowsPerPage] = useState(getRowsPerPageFromLocalStorage(tabKey))
  const [sort, setSort] = useState(getSortFromLocalStorage(tabKey, sortConfig.defaultSortParameters))
  const [filters, setFilters] = useState(getFiltersFromLocalStorage(tabKey))
  const [columnsVisibility, setColumnsVisibility] = useState(getColumnsVisibilityFromLocalStorage(tabKey, columns))

  useEffect(() => { saveFiltersInLocalStorage(tabKey, filters) }, [tabKey, filters])
  useEffect(() => { saveColumnsVisibilityInLocalStorage(tabKey, columnsVisibility) }, [columnsVisibility, tabKey])
  useEffect(() => { saveSortInLocalStorage(tabKey, sort) }, [sort, tabKey])
  useEffect(() => { saveRowsPerPageInLocalStorage(tabKey, rowsPerPage) }, [rowsPerPage, tabKey])
  useEffect(() => { savePageInLocalStorage(tabKey, page) }, [page, tabKey])

  const filteredColumns = useMemo(() => mergeColumnsVisibilityWithColumns(columnsVisibility, columns), [columnsVisibility, columns])

  const rows = useMemo(() => {
    if (pageResponse === null) {
      return []
    }
    if (sort.length > 0 && sortConfig.columnsGroupedBy.includes(sort[0].param)) {
      const colorKeyPath = columns.find(col => col.id === sort[0].param).path
      let lastState = ''
      let background = backgroundColor
      return pageResponse.items.map(item => {
        const colorKeyValue = deepFind(item, colorKeyPath)
        if (lastState !== colorKeyValue) {
          lastState = colorKeyValue
          if (background === backgroundColor) {
            background = ''
          } else {
            background = backgroundColor
          }
        }
        return { rowColor: background, ...item }
      })
    } else {
      return pageResponse.items.map((item, index) => {
        if (index % 2 === 0) {
          return item
        } else {
          return { rowColor: backgroundColor, ...item }
        }
      })
    }
  }, [pageResponse, sortConfig.columnsGroupedBy, sort, columns])

  useEffect(() => {
    const queryParams = {
      page: page,
      rowsPerPage: rowsPerPage,
      sort: sort.length > 0 ? JSON.stringify(sort) : undefined,
      filters: filters.length > 0 ? JSON.stringify(filters) : undefined,
      tabId: tabId
    }
    history.replace({
      pathname: window.location.pathname,
      search: queryString.stringify(queryParams),
    })
  }, [page, rowsPerPage, sort, filters, history, tabId])

  const fetch = useCallback(() =>
      fetchData({ page, rowsPerPage, sort, filters: [...filters, ...hiddenFilters], }),
    [fetchData, filters, page, rowsPerPage, sort, hiddenFilters])

  const refreshContent = useCallback(() => {
    fetch()
      .then(response => onPageResponseChange(response))
      .catch(handleError)
  }, [fetch, handleError, onPageResponseChange])

  const refreshContentInBackground = useCallback(() => {
    if (!pageResponse) {
      return
    }
    fetch()
      .then(response => {
        const updatedItemsIds = response.items.filter(fetchedItem => {
          const currentItem = pageResponse.items.find(it => it.id === fetchedItem.id)
          if (!currentItem) {
            return true
          }
          return backgroundRefresh.shouldHighlightRow(currentItem, fetchedItem)
        }).map(it => it.id)
        onHighlightedIdsChange([...highlightedIds, ...updatedItemsIds])
        onPageResponseChange(response)
      })
      .catch(handleError)
  }, [fetch, handleError, pageResponse, highlightedIds, backgroundRefresh, onPageResponseChange, onHighlightedIdsChange])

  // refreshContent not in dependency array to prevent loop of re-renders
  // eslint-disable-next-line
  useEffect(refreshContent, [fetchData, filters, sort, page, rowsPerPage])

  const handleFiltersChanged = useCallback(newFilters => {
    setFilters(newFilters)
    setPage(0)
  }, [])

  const handleChangePage = useCallback((withScrollTop) => (event, newPage) => {
    setPage(newPage)
    if (withScrollTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  const handleChangeRowsPerPage = useCallback(newRowsPerPage => {
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }, [])

  const handleSortChanged = useCallback(newSort => {
    setSort(newSort)
  }, [])

  const handleColumnsChanged = useCallback(newColumns => {
    const newColumnsVisibility = newColumns.map(col => ({
      id: col.id,
      isVisible: col.isVisible,
    }))
    setColumnsVisibility(newColumnsVisibility)
  }, [])

  const isRowHighlighted = row => highlightedIds.some(id => row.id === id)

  const onRowClickHandler = row => (event) => {
    const textSelection = window.getSelection()
    if (!textSelection.toString()) {
      if (isRowHighlighted(row)) {
        const newHighlightedIds = highlightedIds.filter(id => id !== row.id)
        onHighlightedIdsChange(newHighlightedIds)
        event.stopPropagation()
      } else {
        if (onRowClick) {
          onRowClick(row)
        }
      }
    }
  }

  const pageSelection = useMemo(() => {
    if (selection && pageResponse) {
      const selectedRowsIds = selection.selectedRowsIds
      const pageItemsIds = pageResponse.items.map(item => item.id)
      const isChecked = pageItemsIds.every(id => selectedRowsIds.includes(id))
      const idsToAdd = pageItemsIds.filter(id => !selectedRowsIds.includes(id))
      return ({
        isChecked: () => isChecked,
        onChange: () => isChecked ? selection.onRowsRemove(pageItemsIds) : selection.onRowsSelect(idsToAdd)
      })
    }
  }, [pageResponse, selection])

  const rowSelection = useMemo(() => {
    if (selection) {
      const isSelected = (row) => selection.selectedRowsIds.includes(row.id)
      return {
        isSelected: isSelected,
        onChange: (row) => isSelected(row) ? selection.onRowsRemove([row.id]) : selection.onRowsSelect([row.id])
      }
    }
  }, [selection])

  useImperativeHandle(tableRef, () => ({
    refreshContent,
    refreshContentInBackground,
    filters,
    setFilters: handleFiltersChanged
  }), [refreshContent, refreshContentInBackground, filters, handleFiltersChanged])

  function renderPaginator (withScrollToTop) {
    return <Paginator
      count={pageResponse?.totalItemCount}
      rowsPerPage={rowsPerPage}
      page={page}
      onChangePage={handleChangePage(withScrollToTop)}
    />
  }

  const handleRowRemoved = () => {
    const isOnLastAndEmptyPage = rowsPerPage * page === pageResponse?.totalItemCount - 1
    if (isOnLastAndEmptyPage && page !== 0) {
      setPage(page - 1)
    } else {
      refreshContent()
    }
  }

  return <TableContainerWrapper
    className={classes.tableContainer}
    columnsVisibilityChanged={columnsVisibility}>
    <RowMenuOptionsProvider menu={menu}
                            onDeleteSuccess={handleRowRemoved}>
      <Toolbar className={classes.toolbar} id={'toolbar'}>
        <div className={classes.toolbarContentContainer}>
          <div className={classes.toolbarLeftPanel}>
            <h1 className={classes.title}>
              {`${t(title)} `}
              {pageResponse ? `(${pageResponse.totalItemCount})` : '(...)'}
            </h1>
            <div className={classes.titleNavigation}>
              <RowsNumberSelector
                rowsPerPageOptions={[25, 50, 75, 100]}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
              {
                renderToolbarLeftPanel()
              }
            </div>
          </div>
          <div className={classes.toolbarRightPanel}>
            {renderPaginator(false)}
            {
              addButton && (
                <Tooltip title={addButton.tooltip || ''}>
                  <div className={classes.addButtonContainer}>
                    <ContainedButton
                      onClick={addButton.onClick}
                      label={addButton.label}
                      icon={<AddIcon/>}
                      isDisabled={!addButton.isClickable}
                      className={classes.addButton}/>
                  </div>
                </Tooltip>
              )
            }
          </div>
        </div>
      </Toolbar>
      <Table
        className={classes.table}
        aria-labelledby="tableTitle"
        aria-label="enhanced table"
      >
        <TableHead className={classes.tableHead}>
          <TableHeadRow
            sort={sort}
            onSortChanged={handleSortChanged}
            columns={filteredColumns}
            onColumnsChanged={handleColumnsChanged}
            selection={pageSelection}
          />
          <TableFiltersRow
            columns={filteredColumns}
            filters={filters}
            onFiltersChanged={handleFiltersChanged}
            isSelectionDefined={!!selection}
          />
          <tr className={classes.spacer} id={'table-body-spacer'}/>
        </TableHead>
        <TableBody>
          {
            rows.map(row => (
              <TableBodyRow
                key={row.id}
                row={row}
                onClick={onRowClickHandler(row)}
                columns={filteredColumns}
                isHighlighted={isRowHighlighted(row)}
                selection={rowSelection}
              />
            ))
          }
        </TableBody>
      </Table>
      {
        renderBottomExtraRow()
      }
      <Toolbar className={classes.paginatorToolbar}>
        <div className={'flex-grow-1'}/>
        {renderPaginator(true)}
        <div className={'flex-grow-1'}/>
      </Toolbar>
    </RowMenuOptionsProvider>
  </TableContainerWrapper>
}

const ForwardedTableTabComponent = forwardRef(TableTab)

ForwardedTableTabComponent.displayName = 'TableTab'
ForwardedTableTabComponent.propTypes = {
  ...tableAndTableTabCommonPropTypes,
  ...tableTabPropTypes
}

export default ForwardedTableTabComponent
